import { FunctionComponent, useCallback, useEffect, useMemo, useRef, useState } from "react";

import ReactDOM from "react-dom";

import { faPenToSquare } from "@fortawesome/pro-solid-svg-icons";

import { useTranslation } from "react-i18next";

import { Icon } from "@/components";
import { useGetSendersUsersQuery } from "@/pages/Private/redux/admin/admin.api";
import { Tooltip } from "@/components/Tooltip/Tooltip";
import { Spinner } from "@/components/Spinner/Spinner";
import { Modal } from "@/components/Modals/Modal";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import { authSelector } from "@/pages/Public/redux/auth.slice";
import { updatePersonInInbox } from "@/pages/Private/redux/list/list.slice";
import { useUpdateScheduleSenderMutation } from "@/pages/Private/redux/schedule/schedule.api";

import { ListPerson } from "@/pages/Private/pages/List/schema/list";

import { useViewportClampedPanel } from "@/utils/useViewportClampedPanel";

import { composedPathContains } from "@/utils/getComposedEventTarget";

import {
	listOtherSenders,
	senderConfirmationQuestion,
} from "../../../utils/senderConfirmation.util";
import { isMailboxHealthy } from "../../../utils/resolveMyDefaultSender.util";

import {
	LastOutboundAuthor,
	OutboxSenderInfo as OutboxSenderInfoType,
	SenderIdentity,
	getEditableScheduleIds,
	resolveOutboxCardSender,
} from "../utils/getOutboxSender";

const SILENT_STEP_TYPES = ["linkedinVisitNode"];

type SenderOption = {
	userId: number;
	name: string;
	type: "email" | "linkedin";
	emailId?: number;
	emailAddress?: string;
	/** A mailbox that needs a reconnect: listed, not choosable (PREC-4312). */
	disabled?: boolean;
};

export const OutboxSenderInfo: FunctionComponent<{
	row: ListPerson;
	/**
	 * Read-only sender identity used when no schedule resolves one (pure chat-outreach rows,
	 * PREC-3850 Inbox card) — e.g. the user on the last outbound conversation.
	 */
	fallbackSender?: SenderIdentity | null;
	/** Render nothing instead of the "?" avatar when no sender can be resolved at all. */
	hideWhenUnknown?: boolean;
	/**
	 * Who wrote the message the card previews (PREC-4085). Shown read-only instead of the last
	 * campaign step's sender when that message is newer than the step and nothing is pending.
	 */
	lastOutboundAuthor?: LastOutboundAuthor | null;
	/**
	 * Show who sends without offering to change it — for a surface that only finds a lead, such as
	 * Find a person (PREC-4175). The switcher portals to `document.body`, which the extension's
	 * shadow root cannot style.
	 */
	readOnly?: boolean;
}> = ({ row, fallbackSender, hideWhenUnknown = false, lastOutboundAuthor, readOnly = false }) => {
	const { t } = useTranslation();
	const dispatch = useAppDispatch();
	const { data: sendingUsers } = useGetSendersUsersQuery();
	const currentUserId = useAppSelector(authSelector)?.user?.id;
	const [updateScheduleSender, { isLoading: isUpdating }] = useUpdateScheduleSenderMutation();
	const [isOpen, setIsOpen] = useState(false);
	const [showConfirmModal, setShowConfirmModal] = useState(false);
	const [pendingSelection, setPendingSelection] = useState<SenderOption | null>(null);
	const containerRef = useRef<HTMLDivElement>(null);
	const dropdownRef = useRef<HTMLDivElement>(null);

	// Keeps the portalled panel inside the viewport wherever the trigger sits (PREC-4138).
	useViewportClampedPanel(dropdownRef, isOpen);

	const senderInfo = useMemo<OutboxSenderInfoType | undefined>(() => {
		const resolvedSender = resolveOutboxCardSender(
			row.schedules,
			sendingUsers?.data,
			lastOutboundAuthor
		);

		if (resolvedSender) {
			return readOnly ? { ...resolvedSender, isEditable: false } : resolvedSender;
		}

		if (fallbackSender) {
			return { ...fallbackSender, userId: -1, scheduleId: -1, isEditable: false };
		}

		return undefined;
	}, [row.schedules, sendingUsers?.data, fallbackSender, lastOutboundAuthor, readOnly]);

	const senderInitials = useMemo(() => {
		if (!senderInfo) {
			return "?";
		}

		const first = senderInfo.firstName?.charAt(0) || "";
		const last = senderInfo.lastName?.charAt(0) || "";

		return `${first}${last}` || "?";
	}, [senderInfo]);

	const tooltipContent = useMemo(() => {
		if (!senderInfo) {
			return t("outbox.no_sender");
		}

		const channel = senderInfo.type === "email" ? "Email" : "LinkedIn";
		const address = senderInfo.type === "email" ? ` (${senderInfo.emailAddress})` : "";
		const editHint = senderInfo.isEditable ? ` - ${t("basics.clickToChange")}` : "";

		return `${senderInfo.name} - ${channel}${address}${editHint}`;
	}, [senderInfo, t]);

	const senderOptions = useMemo<SenderOption[]>(() => {
		if (!sendingUsers?.data || !senderInfo) {
			return [];
		}

		const options: SenderOption[] = [];

		// Only senders that can send are choosable: LinkedIn for a user with a session, and a
		// mailbox that needs a reconnect is listed disabled with the hint (PREC-4312).
		for (const user of sendingUsers.data) {
			if (senderInfo.type === "linkedin" && user.unipile?.isLoggedIn) {
				options.push({
					userId: user.id,
					name: `${user.firstName} ${user.lastName}`,
					type: "linkedin",
				});
			} else if (senderInfo.type === "email" && user.emails?.length) {
				for (const email of user.emails) {
					options.push({
						userId: user.id,
						name: `${user.firstName} ${user.lastName}`,
						type: "email",
						emailId: email.id,
						emailAddress: email.email,
						disabled: !isMailboxHealthy(email),
					});
				}
			}
		}

		return options;
	}, [sendingUsers?.data, senderInfo]);

	const hasNonSilentSteps = useMemo(() => {
		if (!row.schedules) {
			return false;
		}

		return (
			row.schedules.some(
				(schedule) =>
					schedule.processedAt !== null &&
					schedule.canceledAt === null &&
					schedule.stepType &&
					!SILENT_STEP_TYPES.includes(schedule.stepType)
			) && !row.schedules.some((schedule) => schedule.conversationId)
		);
	}, [row.schedules]);

	const handleSelect = useCallback(
		(option: SenderOption) => {
			if (!senderInfo) {
				return;
			}

			setIsOpen(false);
			setPendingSelection(option);
			setShowConfirmModal(true);
		},
		[senderInfo]
	);

	const editableScheduleIds = useMemo(() => getEditableScheduleIds(row.schedules), [row.schedules]);

	// Saving a colleague as the sender of the scheduled messages asks the composer's question
	// (PREC-4312, E6) inside this confirmation; picking oneself, any mailbox, does not.
	const senderQuestion = useMemo(() => {
		const others = pendingSelection
			? listOtherSenders([pendingSelection], currentUserId, sendingUsers?.data)
			: [];

		return others.length ? senderConfirmationQuestion(others, editableScheduleIds.length) : null;
	}, [pendingSelection, currentUserId, sendingUsers?.data, editableScheduleIds.length]);

	const handleConfirm = useCallback(async () => {
		if (!senderInfo || !pendingSelection) {
			return;
		}

		setShowConfirmModal(false);

		try {
			let updatedPerson: ListPerson | null = null;

			for (const scheduleId of editableScheduleIds) {
				updatedPerson = await updateScheduleSender({
					scheduleId,
					userId: pendingSelection.userId,
					emailId: pendingSelection.emailId,
				}).unwrap();
			}

			if (updatedPerson) {
				dispatch(
					updatePersonInInbox({
						personId: row.id,
						updatedPerson,
					})
				);
			}
		} catch {
			// Error is handled by RTK Query
		} finally {
			setPendingSelection(null);
		}
	}, [senderInfo, pendingSelection, updateScheduleSender, dispatch, editableScheduleIds, row.id]);

	const handleCancelConfirm = useCallback(() => {
		setShowConfirmModal(false);
		setPendingSelection(null);
	}, []);

	// Close dropdown on outside click
	useEffect(() => {
		if (!isOpen) {
			return;
		}

		const handleClickOutside = (e: MouseEvent) => {
			if (
				containerRef.current &&
				!composedPathContains(containerRef.current, e) &&
				dropdownRef.current &&
				!composedPathContains(dropdownRef.current, e)
			) {
				setIsOpen(false);
			}
		};

		document.addEventListener("mousedown", handleClickOutside);

		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, [isOpen]);

	const isCurrentSender = (option: SenderOption) => {
		if (!senderInfo) {
			return false;
		}

		if (option.type === "email") {
			return option.userId === senderInfo.userId && option.emailId === senderInfo.emailId;
		}

		return option.userId === senderInfo.userId;
	};

	const dropdownPosition = useMemo(() => {
		if (!isOpen || !containerRef.current) {
			return { top: 0, left: 0 };
		}

		const rect = containerRef.current.getBoundingClientRect();

		return {
			top: rect.bottom + 4,
			left: rect.left,
		};
	}, [isOpen]);

	if (hideWhenUnknown && !senderInfo) {
		return null;
	}

	return (
		<div className="flex flex-col gap-1 relative" ref={containerRef}>
			<Tooltip content={tooltipContent}>
				<div
					className={`group bg-blue-action rounded-full flex flex-row items-center justify-center w-[24px] h-[24px] text-white text-xxs font-bold ${
						senderInfo?.isEditable ? "cursor-pointer hover:opacity-80" : "cursor-default"
					}`}
					onClick={(e) => {
						if (senderInfo?.isEditable && !isUpdating) {
							e.stopPropagation();
							setIsOpen(!isOpen);
						}
					}}
				>
					{isUpdating ? (
						<Spinner />
					) : senderInfo?.isEditable ? (
						<>
							<span className="group-hover:hidden">{senderInitials}</span>
							<Icon className="w-2.5 h-2.5 hidden group-hover:block" icon={faPenToSquare} />
						</>
					) : (
						senderInitials
					)}
				</div>
			</Tooltip>
			{isOpen &&
				senderInfo?.isEditable &&
				ReactDOM.createPortal(
					<div
						ref={dropdownRef}
						className="fixed z-[9999] bg-white border border-border-light rounded-lg shadow-lg py-1 min-w-[220px] max-h-[240px] overflow-y-auto"
						style={{ top: dropdownPosition.top, left: dropdownPosition.left }}
					>
						{senderOptions.length === 0 && (
							<div className="px-3 py-2 text-text-meta text-xs">{t("outbox.no_sender")}</div>
						)}
						{senderOptions.map((option, index) => (
							<button
								key={`${option.userId}-${option.emailId ?? "li"}-${index}`}
								className={`w-full text-left px-3 py-2 text-xs hover:bg-brand-white flex flex-col gap-0.5 ${
									isCurrentSender(option) ? "bg-brand-white font-semibold" : ""
								}`}
								onClick={(e) => {
									e.stopPropagation();
									handleSelect(option);
								}}
								disabled={isCurrentSender(option) || option.disabled}
							>
								<span className="text-text">{option.name}</span>
								{option.type === "email" && option.emailAddress && (
									<span className="text-text-meta">{option.emailAddress}</span>
								)}
								{option.type === "linkedin" && <span className="text-text-meta">LinkedIn</span>}
								{option.disabled && (
									<span className="text-status-error">{t("inbox.identity.reconnectMailbox")}</span>
								)}
							</button>
						))}
					</div>,
					document.body
				)}
			{showConfirmModal && (
				<Modal
					handleClose={handleCancelConfirm}
					handleSave={handleConfirm}
					isLoading={isUpdating}
					isOpened={showConfirmModal}
					submitButtonText={t("basics.confirm")}
					title={t("outbox.changeSenderTitle")}
				>
					<div className="flex flex-col gap-3">
						<p className="text-text text-sm">{t("outbox.changeSenderConfirmation")}</p>
						{senderQuestion && (
							<p className="text-sm font-semibold text-text" data-testid="sender-confirm-question">
								{t(senderQuestion.key, senderQuestion.values)}
							</p>
						)}
						{hasNonSilentSteps && (
							<p
								className="text-sm text-status-error font-semibold"
								data-testid="non-silent-warning"
							>
								{t("outbox.changeSenderWarning")}
							</p>
						)}
					</div>
				</Modal>
			)}
		</div>
	);
};
